/**
 * App-level Daily Vehicle Check gate. Wraps routed page content so a locked
 * driver cannot reach jobs/dashboard by direct URL, deep link, back or refresh.
 * Only the check itself and account/help pages stay reachable.
 *
 * An appraisal already in progress on the current route is never interrupted
 * (date rollover, settings change, check becoming required). "In progress" is
 * read from the existing activeWorkflowGuard — no second appraisal-state system.
 * A locked driver can't open an appraisal route, so StepBasedAppraisal never
 * mounts and no workflow is registered; the gate applies again once the
 * workflow ends or the driver leaves its route.
 */
import { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useDailyCheckGate } from "@/hooks/useDailyCheckGate";
import { DailyCheckGate } from "@/components/DailyCheckGate";
import { isPathAllowedWhileLocked } from "@/lib/dailyCheckQuestions";
import { getActiveWorkflow, type ActiveWorkflow } from "@/lib/activeWorkflowGuard";

/** True when an active (non-stale) appraisal workflow belongs to this exact route. */
export function isActiveAppraisalOnPath(pathname: string, wf: ActiveWorkflow | null): boolean {
  return !!wf && typeof wf.route === "string" && wf.route === pathname;
}

export function DailyCheckAccessGate({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { gateLocked, loading } = useDailyCheckGate();

  if (isPathAllowedWhileLocked(pathname)) return <>{children}</>;
  if (isActiveAppraisalOnPath(pathname, getActiveWorkflow())) return <>{children}</>;
  if (gateLocked) return <DailyCheckGate />;
  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center" aria-busy="true">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  return <>{children}</>;
}
